/** school-ai の src/components/TutorPassword/tutorPasswordFormat.ts と同じ判定・表記（school-ai 側は英語表示のため t() で包んでいる）。両サービスでそろえる */
/** お願いのバナーの「詳細はこちら」の行き先（パスワード変更の周知ページ） */
export const TUTOR_PASSWORD_GUIDE_URL =
  "https://app.notion.com/p/mingaku/AI-3f9ef296e7748017844ae012b0aa948c";

const TIME_ZONE = "Asia/Tokyo";
const DAY_MS = 24 * 60 * 60 * 1000;

function jstParts(ms: number) {
  const parts = new Intl.DateTimeFormat("ja-JP", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    weekday: "short"
  }).formatToParts(ms);
  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find(part => part.type === type)?.value ?? "";
  return {
    year: Number(pick("year")),
    month: Number(pick("month")),
    day: Number(pick("day")),
    weekday: pick("weekday")
  };
}

/**
 * 変更期限の最終日を「11月15日（日）」の形で返す。requiredAt は必須化が始まる時刻
 * （最終日の翌日 0 時）なので 1 ミリ秒戻して日付を取る。今年でなければ年を付ける
 */
export function formatPasswordDeadline(requiredAt: number, now: number) {
  const last = jstParts(requiredAt - 1);
  const year = last.year === jstParts(now).year ? "" : `${last.year}年`;
  return `${year}${last.month}月${last.day}日（${last.weekday}）`;
}

/** 期限の最終日まで、日本時間の暦日で何日あるか。最終日の当日は 0 */
export function daysUntilPasswordDeadline(requiredAt: number, now: number) {
  const toDay = (ms: number) => {
    const { year, month, day } = jstParts(ms);
    return Date.UTC(year, month - 1, day);
  };
  return Math.max(0, Math.round((toDay(requiredAt - 1) - toDay(now)) / DAY_MS));
}

export function formatDaysLeft(days: number) {
  return days === 0 ? "本日まで" : `あと${days}日`;
}

/** 使えない文字。記号は使える（スペースを除く ASCII と ¥ 以外が対象） */
const TUTOR_PASSWORD_DISALLOWED_CHARACTER = /[^\x21-\x7E\u00A5]/;
/** 英字以外で使える文字（数字と記号）。英字と、このどちらかを含めれば条件を満たす */
const TUTOR_PASSWORD_DIGIT_OR_SYMBOL = /[\x21-\x40\x5B-\x60\x7B-\x7E\u00A5]/;

/**
 * 入力中に満たしたかを示す条件。4 つすべてを満たすことと
 * isValidTutorPassword が true になることは一致させる。
 * prohibits の条件（使ってはいけない文字）は、未入力のまま送信しても誤りとして示さない
 */
export const TUTOR_PASSWORD_RULES = [
  {
    id: "length",
    label: "8〜64文字",
    isMet: (value: string) => value.length >= 8 && value.length <= 64,
    // 入力途中の「まだ短い」は誤りではないので、超えたときだけ即座に知らせる
    isBroken: (value: string) => value.length > 64,
    prohibits: false
  },
  {
    id: "letter",
    label: "英字（a〜z、A〜Z）を含む",
    isMet: (value: string) => /[A-Za-z]/.test(value),
    isBroken: () => false,
    prohibits: false
  },
  {
    id: "digitOrSymbol",
    label: "数字または記号を含む",
    isMet: (value: string) => TUTOR_PASSWORD_DIGIT_OR_SYMBOL.test(value),
    isBroken: () => false,
    prohibits: false
  },
  {
    id: "charset",
    label: "スペース・全角文字は使わない",
    isMet: (value: string) =>
      value.length > 0 && !TUTOR_PASSWORD_DISALLOWED_CHARACTER.test(value),
    isBroken: (value: string) =>
      TUTOR_PASSWORD_DISALLOWED_CHARACTER.test(value),
    prohibits: true
  }
] as const;
